import { useRef, useState } from 'react';
import { uploadIndirectPaymentImport } from '../../services/kollectaApi';
import styles from '../UploadFileAndList/style.module.css';
import modalStyles from './style.module.css';
import Modal from '../Modal';
import ReactLoading from 'react-loading';
import success from '../../assets/images/dataSuccess.svg';
import error from '../../assets/images/error.svg';
import warning from '../../assets/images/warningOrange.svg';
import uploadIcon from '../UploadFileAndList/assets/upload-icon.svg';
import * as xlsx from 'xlsx';

const IndirectPaymentUpload = ({ exampleFile }) => {
  const filesElement = useRef(null);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [popUpFeedBack, setPopUpFeedBack] = useState({
    loading: false,
    confirm: false,
    success: false,
    typeError: false,
    message: '',
    totalDocuments: 0,
    dataForm: null,
    result: null,
  });

  const validateSelectedFile = async (file) => {
    if (!file) {
      setPopUpFeedBack((prev) => ({
        ...prev,
        loading: false,
        typeError: true,
        message: 'Selecione um arquivo Excel.',
      }));
      return;
    }

    const dataForm = new FormData();
    dataForm.append('file', file);
    setFiles([file]);
    setPopUpFeedBack((prev) => ({ ...prev, loading: true, typeError: false, message: '' }));

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = xlsx.read(e.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const sheetData = xlsx.utils.sheet_to_json(worksheet, { header: 1 });

        if (!sheetData.length) {
          setPopUpFeedBack((prev) => ({
            ...prev,
            loading: false,
            typeError: true,
            message: 'Arquivo Excel está vazio.',
          }));
          return;
        }

        const headers = (sheetData[0] || []).map((header) => String(header || '').trim());
        const cpfIndex = headers.indexOf('cpf_ou_cnpj');

        if (cpfIndex === -1) {
          setPopUpFeedBack((prev) => ({
            ...prev,
            loading: false,
            typeError: true,
            message: 'A planilha precisa ter a coluna cpf_ou_cnpj.',
          }));
          return;
        }

        const rows = sheetData
          .slice(1)
          .filter((row) => row.some((cell) => cell !== undefined && cell !== ''));

        const documents = rows
          .map((row) => String(row[cpfIndex] ?? '').replace(/[^\d]/g, ''))
          .filter(Boolean);

        if (!documents.length) {
          setPopUpFeedBack((prev) => ({
            ...prev,
            loading: false,
            typeError: true,
            message: 'Nenhum CPF/CNPJ válido encontrado na planilha.',
          }));
          return;
        }

        setPopUpFeedBack((prev) => ({
          ...prev,
          loading: false,
          confirm: true,
          totalDocuments: new Set(documents).size,
          dataForm,
        }));
      } catch (validationError) {
        setPopUpFeedBack((prev) => ({
          ...prev,
          loading: false,
          typeError: true,
          message: validationError.message || 'Não foi possível validar o arquivo.',
        }));
      }
    };

    reader.readAsBinaryString(file);
  };

  const validateFile = async () => {
    const selectedFiles = filesElement.current?.files;
    await validateSelectedFile(selectedFiles?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFile = event.dataTransfer.files?.[0];
    if (filesElement.current && event.dataTransfer.files?.length) {
      filesElement.current.files = event.dataTransfer.files;
    }
    validateSelectedFile(droppedFile);
  };

  const sendFile = async (dataForm) => {
    try {
      setPopUpFeedBack((prev) => ({ ...prev, loading: true, confirm: false }));
      const response = await uploadIndirectPaymentImport(dataForm);
      setPopUpFeedBack((prev) => ({
        ...prev,
        loading: false,
        success: true,
        result: response.data,
      }));
    } catch (err) {
      setPopUpFeedBack((prev) => ({
        ...prev,
        loading: false,
        typeError: true,
        message: err?.data?.message || err?.response?.data?.message || 'Não foi possível atualizar o status das dívidas.',
      }));
    }
  };

  return (
    <div className={styles.uploadFileContainer}>
      <input
        className={styles.inputFile}
        id="indirect-payment-file"
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={validateFile}
        ref={filesElement}
      />

      <label
        htmlFor="indirect-payment-file"
        className={`${styles.dropzone} ${isDragging ? styles.dropzoneActive : ''} ${popUpFeedBack.loading ? styles.dropzoneDisabled : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <img
          className={styles.uploadIcon}
          src={uploadIcon}
          alt=""
          width={32}
          height={32}
        />
        <p className={styles.dropzoneTitle}>
          Arraste e solte os arquivos aqui
          {' '}
          <span>ou procure no computador</span>
        </p>
        <p className={styles.dropzoneHint}>Arquivos ilimitados, até 20 MB cada</p>
      </label>

      <p className={styles.formatHint}>
        Arquivo no formato Excel
        {exampleFile && (
          <>
            {' · '}
            <a href={exampleFile} target="_blank" rel="noreferrer">
              baixar modelo
            </a>
          </>
        )}
      </p>

      {files.length > 0 && (
        <p className={styles.enqueueNotice}>{files[0].name}</p>
      )}

      {popUpFeedBack.loading && (
        <Modal onClose={() => setPopUpFeedBack((prev) => ({ ...prev, loading: false }))}>
          <div className={modalStyles.modalContainer}>
            <p>Atualizando o status das dívidas para Pagamento indireto...</p>
            <ReactLoading type="spin" color="#293053" height={50} width={50} />
          </div>
        </Modal>
      )}

      {popUpFeedBack.confirm && (
        <Modal onClose={() => setPopUpFeedBack((prev) => ({ ...prev, confirm: false }))}>
          <div className={modalStyles.modalContainer}>
            <img src={warning} alt="warning" />
            <p>
              Você está prestes a <b>atualizar o status</b> das dívidas de{' '}
              <b>{popUpFeedBack.totalDocuments} CPF/CNPJ</b> para{' '}
              <b>Pagamento indireto</b>.
            </p>
            <p className={modalStyles.confirmHint}>
              Isso não cadastra novas dívidas — apenas altera o status das existentes.
            </p>
            <button
              className={modalStyles.btnSubmit}
              onClick={() => sendFile(popUpFeedBack.dataForm)}
            >
              Confirmar atualização de status
            </button>
          </div>
        </Modal>
      )}

      {popUpFeedBack.success && (
        <Modal onClose={() => setPopUpFeedBack((prev) => ({ ...prev, success: false }))}>
          <div className={modalStyles.modalContainer}>
            <img src={success} alt="success" />
            <p>
              Status atualizado para <b>Pagamento indireto</b> com sucesso.
            </p>
            <p>
              Documentos na planilha: <b>{popUpFeedBack.result?.totalDocuments ?? 0}</b>
              <br />
              Dívidas atualizadas: <b>{popUpFeedBack.result?.updated ?? 0}</b>
            </p>
          </div>
        </Modal>
      )}

      {popUpFeedBack.typeError && (
        <Modal onClose={() => setPopUpFeedBack((prev) => ({ ...prev, typeError: false }))}>
          <div className={modalStyles.modalContainer}>
            <img src={error} alt="error" />
            <p>Não foi possível processar a atualização de status.</p>
            <p>{popUpFeedBack.message}</p>
            <button
              className={modalStyles.btnSubmit}
              onClick={() => setPopUpFeedBack((prev) => ({ ...prev, typeError: false }))}
            >
              Fechar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default IndirectPaymentUpload;
